import React, { useState } from 'react';
import { LOGIN_ENDPOINT, SEND_OTP_ENDPOINT, VERIFY_RESET_PASSWORD_ENDPOINT } from '../../config/api';
import { encryptData } from '../../utils/encrypt';
import { FaLock, FaMobileAlt, FaKey, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';

function Login({ onLoginSuccess }) {
  const [mode, setMode] = useState('login');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // For reset
  const [resetMobile, setResetMobile] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(LOGIN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, password })
      });
      const data = await res.json();
      if (res.ok) {
        const encrypted = encryptData({
          user_id: data.user.user_id,
          user_name: data.user.user_name,
          user_token: data.user.user_token || '',
          modules: data.user.modules || []
        });
        localStorage.setItem('suyog_user', encrypted);
        onLoginSuccess();
      } else {
        setError(data.error || 'Invalid credentials');
      }
    } catch {
      setError('Server error');
    }
  };

  const handleSendOtp = async () => {
    setResetMessage('');
    if (!resetMobile) return setResetMessage('Mobile number is required.');

    try {
      const res = await fetch(SEND_OTP_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: resetMobile })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setOtpSent(true);
        setResetMessage('OTP sent to WhatsApp.');
      } else {
        setResetMessage(data.error || 'Failed to send OTP');
      }
    } catch {
      setResetMessage('Server error while sending OTP');
    }
  };

  const handleVerifyReset = async () => {
    setResetMessage('');
    if (!otp || !newPassword) {
      return setResetMessage('OTP and new password are required.');
    }

    try {
      const res = await fetch(VERIFY_RESET_PASSWORD_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: resetMobile,
          otp,
          new_password: newPassword
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResetMessage('Password reset successfully. You can now login.');
        setMode('login');
        setOtpSent(false);
        setOtp('');
        setNewPassword('');
        setResetMobile('');
      } else {
        setResetMessage(data.error || 'Failed to reset password');
      }
    } catch {
      setResetMessage('Server error while resetting password');
    }
  };

  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100 bg-light">
      <div className="card shadow-sm p-4" style={{ width: '100%', maxWidth: '420px' }}>
        <h4 className="text-center mb-4 fw-bold">
          {mode === 'login' ? <FaLock className="me-2" /> : <FaKey className="me-2" />}
          {mode === 'login' ? 'Login to Suyog ERP' : 'Reset Password'}
        </h4>

        {mode === 'login' && (
          <>
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <label className="form-label">Mobile Number</label>
                <div className="input-group">
                  <span className="input-group-text"><FaMobileAlt /></span>
                  <input
                    type="text"
                    className="form-control"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="Enter mobile number"
                    required
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Password</label>
                <div className="input-group">
                  <span className="input-group-text"><FaLock /></span>
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary w-100">Login</button>
            </form>
            <div className="text-center mt-3">
              <button className="btn btn-link btn-sm" onClick={() => setMode('reset')}>
                Forgot Password?
              </button>
            </div>
          </>
        )}

        {mode === 'reset' && (
          <>
            {resetMessage && (
              <div className={`alert ${resetMessage.includes('successfully') ? 'alert-success' : 'alert-danger'}`}>
                {resetMessage}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Mobile Number</label>
              <input
                type="text"
                className="form-control"
                value={resetMobile}
                onChange={(e) => setResetMobile(e.target.value)}
                placeholder="Enter registered mobile"
              />
            </div>

            {otpSent && (
              <>
                <div className="mb-3">
                  <label className="form-label">OTP</label>
                  <input
                    type="text"
                    className="form-control"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter OTP"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>
              </>
            )}

            {!otpSent ? (
              <button className="btn btn-primary w-100" onClick={handleSendOtp}>Send OTP</button>
            ) : (
              <button className="btn btn-success w-100" onClick={handleVerifyReset}>
                <FaCheckCircle className="me-1" /> Reset Password
              </button>
            )}

            <div className="text-center mt-3">
              <button className="btn btn-link btn-sm" onClick={() => setMode('login')}>
                <FaArrowLeft className="me-1" /> Back to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
